/**
 * BreadcrumbContext
 *
 * Context for managing dynamic breadcrumbs throughout the application.
 * Allows pages to set their own breadcrumbs while providing route-based defaults.
 *
 * @example
 * ```tsx
 * // In a page component
 * function CandidateDetail() {
 *   const { setBreadcrumbs } = useBreadcrumbs()
 *
 *   useEffect(() => {
 *     setBreadcrumbs([
 *       { label: "Candidates", href: "/candidates" },
 *       { label: candidate.name } // Current page - no href
 *     ])
 *   }, [candidate])
 * }
 * ```
 */

import * as React from "react"

/**
 * Breadcrumb item type
 */
export interface BreadcrumbItem {
	label: string
	href?: string
}

/**
 * Breadcrumb context value
 */
interface BreadcrumbContextValue {
	breadcrumbs: BreadcrumbItem[]
	setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void
	/** Sets route-level defaults that won't overwrite custom breadcrumbs set by child pages */
	setDefaultBreadcrumbs: (breadcrumbs: BreadcrumbItem[], routeKey: string) => void
}

/**
 * BreadcrumbContext for managing dynamic breadcrumbs throughout the app
 */
const BreadcrumbContext = React.createContext<BreadcrumbContextValue | undefined>(undefined)

/**
 * Props for BreadcrumbProvider
 */
interface BreadcrumbProviderProps {
	children: React.ReactNode
	defaultBreadcrumbs?: BreadcrumbItem[]
}

/**
 * BreadcrumbProvider component
 */
export function BreadcrumbProvider({ children, defaultBreadcrumbs = [] }: Readonly<BreadcrumbProviderProps>) {
	const [breadcrumbs, setBreadcrumbsState] = React.useState<BreadcrumbItem[]>(defaultBreadcrumbs)
	const isCustomRef = React.useRef(false)
	const lastDefaultKeyRef = React.useRef("")

	const setBreadcrumbs = React.useCallback((items: BreadcrumbItem[]) => {
		isCustomRef.current = true
		setBreadcrumbsState(items)
	}, [])

	const setDefaultBreadcrumbs = React.useCallback((items: BreadcrumbItem[], routeKey: string) => {
		if (routeKey !== lastDefaultKeyRef.current) {
			// Route changed — reset custom flag and apply new defaults
			lastDefaultKeyRef.current = routeKey
			isCustomRef.current = false
			setBreadcrumbsState(items)
		}
		// Same route + custom was set by a child page → don't overwrite
	}, [])

	const contextValue = React.useMemo(
		() => ({
			breadcrumbs,
			setBreadcrumbs,
			setDefaultBreadcrumbs,
		}),
		[breadcrumbs, setBreadcrumbs, setDefaultBreadcrumbs]
	)

	return <BreadcrumbContext.Provider value={contextValue}>{children}</BreadcrumbContext.Provider>
}

/**
 * Hook to access and update breadcrumbs
 */
export function useBreadcrumbs() {
	const context = React.useContext(BreadcrumbContext)
	if (!context) {
		throw new Error("useBreadcrumbs must be used within BreadcrumbProvider")
	}
	return context
}

/**
 * Safe version of useBreadcrumbs that works outside BreadcrumbProvider
 *
 * Returns a no-op setter and empty breadcrumbs when used outside the provider.
 * Use this in components that may be rendered in both SinglePageRoutes
 * (no provider) and Main Routes (has provider) contexts.
 *
 * @returns {BreadcrumbContextValue} Breadcrumbs context with safe fallbacks
 * @returns {BreadcrumbItem[]} breadcrumbs - Current breadcrumbs (empty array if no provider)
 * @returns {Function} setBreadcrumbs - Sets breadcrumbs (no-op if no provider)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { setBreadcrumbs } = useSafeBreadcrumbs()
 *
 *   useEffect(() => {
 *     // Safe to call - no error if provider missing
 *     setBreadcrumbs([
 *       { label: "Home", href: "/" },
 *       { label: "Current Page" }
 *     ])
 *   }, [setBreadcrumbs])
 * }
 * ```
 */
export function useSafeBreadcrumbs(): BreadcrumbContextValue {
	const context = React.useContext(BreadcrumbContext)

	// Return safe defaults if provider is not available
	if (!context) {
		return {
			breadcrumbs: [],
			setBreadcrumbs: () => {}, // no-op
			setDefaultBreadcrumbs: () => {}, // no-op
		}
	}

	return context
}
